import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
} from "react";
import { useToast, useTranslations } from "@jugl-web/utils";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { invoicesApi } from "@web-src/modules/api/createApi";
import { InvoicesClientDefaultsPageContext } from "./InvoicesClientDefaultsPageContext";
import { clientDefaultsReducer } from "./clientDefaultsReducer";
import { useInvoicesConfigList } from "../../InvoicesBusinessInformationPage/hooks/useInvoicesConfigList";

export const InvoicesClientDefaultsPageProvider = ({
  children,
}: PropsWithChildren) => {
  const { customersFormFieldApi } = useRestApiProvider();
  const { entity } = useEntitySelectedProvider();
  const { configId } = useInvoicesConfigList();

  const invoicesConfig = invoicesApi.useGetConfigQuery({
    entityId: entity.id,
    configId,
  });

  const data = invoicesConfig.data?.data;

  const customFields = data?.custom_field;

  const { t } = useTranslations();
  const { toast } = useToast({ variant: "web" });
  const [items, dispatchItemsReducer] = useReducer(clientDefaultsReducer, []);
  const { data: itemsData } = customersFormFieldApi.useFormFieldsQuery({
    entityId: entity.id,
  });

  useEffect(() => {
    if (itemsData?.data && customFields) {
      const dataItems = [...itemsData.data];
      const sortedItems = dataItems.sort((a, b) => a.pos - b.pos);

      dispatchItemsReducer({
        type: "SET_ITEMS",
        payload: sortedItems.map((item) => ({
          ...item,
          visible: customFields.includes(item.id),
        })),
      });
    }
  }, [itemsData, customFields]);

  const submitForm = useCallback(async () => {
    // TODO: filter items and get those that are visible
    // TODO: update config passing uuid[]
    // TODO: handle position basing
    try {
      toast(
        t({
          id: "invoices.clientDefaults.save-success",
          defaultMessage: "Client defaults saved successfully",
        }),
        { variant: "success" }
      );
    } catch {
      toast(
        t({
          id: "invoices.clientDefaults.save-error",
          defaultMessage: "Error saving client defaults",
        }),
        { variant: "error" }
      );
    }
  }, [t, toast]);

  const value = useMemo(
    () => ({
      items,
      dispatchItemsReducer,
      submitForm,
    }),
    [items, dispatchItemsReducer, submitForm]
  );

  return (
    <InvoicesClientDefaultsPageContext.Provider value={value}>
      {children}
    </InvoicesClientDefaultsPageContext.Provider>
  );
};
